import React from 'react';

import { EllipsisVerticalIcon, SquarePenIcon, CopyIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { collectionsEndpoints } from '@/api/endpoints';
import { EditableText } from '@/components/atoms/EditableText/EditableText';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useCollections from '@/hooks/useCollections';
import { openDeleteCollectionModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setCollectionId } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { appendEllipsis } from '../../helper';

export const DatasourcesDocumentsBreadcrumbs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const { collectionId } = useParams();
  const { collection, updateCollection } = useCollections(collectionId);

  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.DELETE)
  );

  // Validation schema for collection name in breadcrumb
  const collectionNameSchema = React.useMemo(() => {
    return yup
      .object()
      .shape({
        inputField: createNameFieldSchema({
          getEndpoint: collectionsEndpoints.checkName,
          excludeName: collection?.name,
        }),
      });
  }, [collection?.name]);

  const handleCollectionNameSave = (newName: string) => {
    if (!collection || !newName.trim() || newName === collection.name) {
      return;
    }

    updateCollection({ collection_id: collection.collection_id, name: newName.trim() });
  };

  return (
    <Breadcrumb>
      <BreadcrumbList className="min-w-0">
        <BreadcrumbItem className="shrink-0 max-xl:hidden">
          <BreadcrumbLink asChild>
            <Link to={`/${slug}/knowledge-bases`} className="breadcrumb-link">
              {t('common.collections')}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="shrink-0 max-xl:hidden" />
        <BreadcrumbItem className="-mx-1 min-w-0 flex-1">
          <BreadcrumbPage className="min-w-0 gap-0.5 border-0 p-0">
            <EditableText
              defaultValue={collection?.name || ''}
              onSubmit={handleCollectionNameSave}
              canEdit={true}
              variant="body-regular"
              placeholder="Collection Name"
              validationSchema={collectionNameSchema}
              overflowEllipsis
              className="-mx-px"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  className="h-6 w-6"
                  onClick={() => {}}
                >
                  <EllipsisVerticalIcon size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="start">
                <DropdownMenuItem
                  onClick={() => {
                    dispatch(setSidebarContent('create_collection'));
                  }}
                >
                  <SquarePenIcon size={16} className="text-foreground-muted" />{' '}
                  {appendEllipsis(t('common.rename'))}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(collectionId);
                      toast.success(t('common.copied'), { duration: 1000 });
                    } catch {
                      toast.error(t('collections.error_copying_id'), { duration: 4000 });
                    }
                  }}
                >
                  <CopyIcon size={16} className="text-foreground-muted" />
                  {t('collections.copy_id')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => {
                    dispatch(setCollectionId(collectionId));
                    dispatch(openDeleteCollectionModal());
                  }}
                  disabled={!canDelete}
                >
                  <Trash2Icon size={16} />
                  {appendEllipsis(t('common.delete'))}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
